<template>
  <Teleport to="body">
    <Transition name="cdlg">
      <div
        v-if="active"
        class="cdlg"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`cdlg-title-${activeKey}`"
        @click.self="onCancel"
        @keydown.esc="onCancel"
      >
        <div class="cdlg__panel">
          <header class="cdlg__head">
            <span :class="['cdlg__icon', { 'cdlg__icon--danger': active.danger }]" aria-hidden="true">
              {{ active.danger ? "!" : "?" }}
            </span>
            <h2 :id="`cdlg-title-${activeKey}`">{{ active.title }}</h2>
          </header>
          <p v-if="active.body" class="cdlg__body">{{ active.body }}</p>
          <footer class="cdlg__foot">
            <button
              ref="cancelBtn"
              type="button"
              class="cdlg__btn cdlg__btn--ghost"
              @click="onCancel"
            >
              {{ active.cancelLabel || "Cancel" }}
            </button>
            <button
              type="button"
              :class="['cdlg__btn', active.danger ? 'cdlg__btn--danger' : 'cdlg__btn--primary']"
              @click="onConfirm"
            >
              {{ active.confirmLabel || "Confirm" }}
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useConfirm } from "../composables/useConfirm";

const { active, accept, reject } = useConfirm();
const cancelBtn = ref<HTMLButtonElement | null>(null);

// Re-key the title id whenever a new dialog opens so screen readers see
// it as a new message.
const activeKey = computed(() => (active.value ? Date.now() : 0));

watch(active, async (next) => {
  if (next) {
    await nextTick();
    cancelBtn.value?.focus();
  }
});

function onCancel() {
  reject();
}
function onConfirm() {
  accept();
}
</script>

<style scoped>
.cdlg {
  position: fixed;
  inset: 0;
  background: color-mix(in oklab, black 50%, transparent);
  display: grid;
  place-items: center;
  z-index: 1100;
  padding: var(--ram-space-4, 16px);
}
.cdlg__panel {
  width: min(420px, 100%);
  background: var(--ram-color-surface-raised);
  border: 1px solid var(--ram-color-border);
  border-radius: var(--ram-radius-xl, 16px);
  box-shadow: var(--ram-shadow-lg, 0 12px 32px rgba(0, 0, 0, 0.18));
  padding: var(--ram-space-5, 20px) var(--ram-space-6, 24px);
  display: flex;
  flex-direction: column;
  gap: var(--ram-space-3, 12px);
}
.cdlg__head {
  display: flex;
  align-items: center;
  gap: var(--ram-space-3, 12px);
}
.cdlg__head h2 {
  margin: 0;
  font-size: var(--ram-text-lg, 16px);
  font-weight: 600;
  color: var(--ram-color-text-primary);
}
.cdlg__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: color-mix(in oklab, var(--ram-color-action-500) 14%, transparent);
  color: var(--ram-color-action-600, var(--ram-color-action-500));
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
}
.cdlg__icon--danger {
  background: color-mix(in oklab, var(--ram-color-danger) 14%, transparent);
  color: var(--ram-color-danger);
}
.cdlg__body {
  margin: 0;
  color: var(--ram-color-text-secondary);
  font-size: var(--ram-text-sm, 14px);
  line-height: 1.5;
}
.cdlg__foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--ram-space-2, 8px);
  padding-top: var(--ram-space-2, 8px);
}
.cdlg__btn {
  padding: 8px 16px;
  border-radius: var(--ram-radius-md, 8px);
  font: inherit;
  font-weight: 600;
  font-size: var(--ram-text-sm, 14px);
  cursor: pointer;
  border: 1px solid transparent;
  transition: filter 0.15s, background 0.15s;
}
.cdlg__btn:focus-visible {
  outline: 2px solid var(--ram-color-action-500);
  outline-offset: 2px;
}
.cdlg__btn--ghost {
  background: var(--ram-color-surface);
  border-color: var(--ram-color-border);
  color: var(--ram-color-text-primary);
}
.cdlg__btn--ghost:hover { background: var(--ram-color-surface-muted); }
.cdlg__btn--primary {
  background: linear-gradient(135deg, var(--ram-color-action-500), var(--ram-color-action-600));
  color: var(--ram-color-on-action);
}
.cdlg__btn--primary:hover { filter: brightness(1.06); }
.cdlg__btn--danger {
  background: var(--ram-color-danger);
  color: white;
}
.cdlg__btn--danger:hover { filter: brightness(1.06); }

.cdlg-enter-active, .cdlg-leave-active {
  transition: opacity 0.18s ease;
}
.cdlg-enter-active .cdlg__panel, .cdlg-leave-active .cdlg__panel {
  transition: transform 0.18s ease;
}
.cdlg-enter-from, .cdlg-leave-to { opacity: 0; }
.cdlg-enter-from .cdlg__panel { transform: scale(0.95); }
</style>
